/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import * as React from 'react';

import { Module, Node } from '@msdyn365-commerce-modules/utilities';

import { IAccountPreference, IAccountPreferences, IAccountProfileItem } from '@msdyn365-commerce-modules/account-management';

import { validatePhoneNumberWithCountryCode } from '../../common/utils/phone-number-utils';
import { IAccountProfileViewProps } from './fm-account-profile';
import { IFmAccountProfileProps } from './fm-account-profile.props.autogenerated';

const AccountPreferencesSection: React.FC<IAccountPreferences> = ({ accountPreferences, heading, personalization, extraPreferences }) => {
    return (
        <Node {...accountPreferences}>
            {heading}
            <AccountPreferenceSection {...personalization} />
            {extraPreferences &&
                extraPreferences.map((preferenceSecton, index) => {
                    return <AccountPreferenceSection {...preferenceSecton} key={index} />;
                })}
        </Node>
    );
};

const AccountPreferenceSection: React.FC<IAccountPreference> = ({
    accountPreference,
    heading,
    description,
    buttonWrapper,
    buttonYesText,
    button,
    buttonNoText
}) => {
    return (
        <Node {...accountPreference}>
            {heading}
            {description}
            <Node {...buttonWrapper}>
                {buttonNoText}
                {button}
                {buttonYesText}
            </Node>
        </Node>
    );
};

const AccountProfileSection: React.FC<IAccountProfileItem> = ({ AccountProfileItem, heading, links, description }) => {
    return (
        <Node {...AccountProfileItem}>
            {heading}
            {description}
            {links}
        </Node>
    );
};

const AccountProfileView: React.FC<IAccountProfileViewProps & IFmAccountProfileProps<{}>> = props => {
    const {
        AccountProfile,
        accountProfileWrapper,
        heading,
        emailSection,
        nameSection,
        phoneSection,
        preferenceSection,
        customerAttributesWrapper,
        customerAttributesSection,
        emailConsentsSection,
        emailConsentsWrapper,
        phoneConsentsSection,
        phoneConsentsWrapper
    } = props;

    React.useEffect(() => {
        const bd_input: HTMLInputElement = document.getElementsByClassName(
            'ms-account-profile__attributes-element ms-account-profile__attributes_input ms-account-profile__attributes_input-edit-false ms-account-profile__Birthday-input'
        )[0] as HTMLInputElement;

        if (bd_input && props.context.request.canonicalDomain !== 'us.venchi.com') {
            document.getElementsByClassName('ms-account-profile__attributes-edit-button msc-btn')[0].addEventListener('click', () => {
                bd_input.setAttribute('type', 'date');
            });
        }
    }, []);

    const showPhoneConsentSection = React.useMemo(
        () =>
            props.data.customerInformation.result?.Phone && validatePhoneNumberWithCountryCode(props.data.customerInformation.result.Phone),
        []
    );

    return (
        <Module {...AccountProfile}>
            {heading}
            <div className={'row row-fullwidth'}>
                <div className={'container'}>
                    <div className={'row'}>
                        <div className={'col-lg-4 ms-account-profile__main-info'}>
                            <div>
                                {accountProfileWrapper && (
                                    <Node {...accountProfileWrapper}>
                                        <AccountProfileSection {...emailSection} />
                                        <AccountProfileSection {...nameSection} />
                                        <AccountProfileSection {...phoneSection} />
                                        {customerAttributesWrapper && (
                                            <Node {...customerAttributesWrapper}>{customerAttributesSection}</Node>
                                        )}
                                    </Node>
                                )}
                            </div>
                        </div>
                        {/* <div className={'col-lg-4 ms-account-profile__main-info extra'}>
                            {customerAttributesWrapper && <Node {...customerAttributesWrapper}>{customerAttributesSection}</Node>}
                        </div> */}
                        <div className={'col-lg-4 ms-account-profile__main-info extra'}>
                            {emailConsentsWrapper && <Node {...emailConsentsWrapper}>{emailConsentsSection}</Node>}
                        </div>
                        {showPhoneConsentSection && (
                            <div className={'col-lg-4 ms-account-profile__main-info extra'}>
                                {phoneConsentsWrapper && <Node {...phoneConsentsWrapper}>{phoneConsentsSection}</Node>}
                            </div>
                        )}
                        <div className={'col-lg-4 ms-account-profile__preferences'}>
                            <AccountPreferencesSection {...preferenceSection} />
                        </div>
                    </div>
                </div>
            </div>
        </Module>
    );
};

export default AccountProfileView;
